import { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { useEffect, useState } from 'react';

import { Auth0User, Auth0UserKey } from '@externalTypes/auth0';

import { organizationSelected } from '@/redux/slices/loggedUserSlice';
import {
  useLazyGetMyUserDataQuery,
  useLazyGetMyUserIdentitiesQuery,
} from '@/redux/slices/userApiSlice';
import { useAppDispatch } from '@/redux/store';
import { ORG_SELECTOR_ENABLED_CONNECTIONS } from '@/types/queryParamKeys';
import {
  CallerIdentityWithOrganizations,
  LoggedUser,
  OrganizationUserRoleSlugs,
  OrgSelectionChoice,
} from '@/types/users';

type RequestUserDataProps = {
  auth0UserAuthenticated: boolean;
  auth0User?: Auth0User;
  loggedUser: LoggedUser | null;
  selectedOrgId?: string | null;
};

/**
 * Removes any orgs from the list where the user has a Product Support role.
 */
const filterProdSupportOrgs = (
  orgs: CallerIdentityWithOrganizations['organizations']
) => {
  const filteredOrgs: CallerIdentityWithOrganizations['organizations'] = {};
  Object.keys(orgs).forEach(orgId => {
    const org = orgs[orgId];
    if (
      org &&
      org.roles &&
      org.roles.some(
        role => role.slug !== OrganizationUserRoleSlugs.PRODUCT_SUPPORT
      )
    ) {
      filteredOrgs[orgId] = org;
    }
  });
  return filteredOrgs;
};

export const useRequestUserData = ({
  auth0UserAuthenticated,
  auth0User,
  loggedUser,
  selectedOrgId,
}: RequestUserDataProps) => {
  const dispatch = useAppDispatch();
  const [isRequestingUserData, setIsRequestingUserData] = useState(false);
  const [userDataRequestCompleted, setUserDataRequestCompleted] =
    useState(false);
  const [orgSelectionRequired, setOrgSelectionRequired] = useState(false);
  const [orgSelectionChoices, setOrgSelectionChoices] = useState<
    OrgSelectionChoice[]
  >([]);

  const [
    getMyUserData,
    { isError: myUserDataError, error: myUserDataErrorDetails },
  ] = useLazyGetMyUserDataQuery();
  // Used instead of getMyUserData when the connection requires an org
  // selection (see ORG_SELECTOR_ENABLED_CONNECTIONS): /v1/users/me 400s when
  // the caller's auth0_id maps to more than one distinct user record (e.g.
  // multi-tenant isolved users) - /v1/users/me/identities returns one entry
  // per user record instead, with no disambiguation.
  const [
    getMyUserIdentities,
    { isError: myUserIdentitiesError, error: myUserIdentitiesErrorDetails },
  ] = useLazyGetMyUserIdentitiesQuery();

  // Only one of the two queries above is ever triggered per session, so at
  // most one of these will be true/set.
  const isUnauthorizedError: boolean =
    (myUserDataErrorDetails as FetchBaseQueryError)?.status === 401 ||
    (myUserIdentitiesErrorDetails as FetchBaseQueryError)?.status === 401;

  useEffect(() => {
    const requestUserData = async () => {
      if (
        !userDataRequestCompleted &&
        !isRequestingUserData &&
        auth0UserAuthenticated &&
        auth0User &&
        loggedUser?.email &&
        selectedOrgId !== undefined // null would be an intentional value set after sessionStorage check
      ) {
        // Start requests
        setIsRequestingUserData(true);

        const connectionName = auth0User[Auth0UserKey.CONNECTION_NAME];
        const auth0Organizations = auth0User[Auth0UserKey.ORGANIZATIONS];

        // Auth0 already dedupes to a single org when there's no ambiguity (e.g external_id provided),
        // so the identities endpoint is only needed when the claim lists
        // more than one org.
        const usesIdentitiesEndpoint =
          !!connectionName &&
          ORG_SELECTOR_ENABLED_CONNECTIONS.includes(connectionName) &&
          (auth0Organizations?.length ?? 0) > 1;

        // Skip setting the org if it's already set, we still need to make one
        // of the calls below either way. If the orgId is null, this means
        // we've finished checking the sessionStorage but nothing was found.
        let organizations:
          | CallerIdentityWithOrganizations['organizations']
          | undefined;

        if (usesIdentitiesEndpoint) {
          const { data: identities } = await getMyUserIdentities();
          organizations = identities
            ? Object.assign(
                {},
                ...identities.map(identity => identity.organizations)
              )
            : undefined;
        } else {
          // Note: getMyUserData is always refetched to avoid stale role/permission data.
          const { data: userData } = await getMyUserData();
          organizations = userData?.organizations;
        }

        if (organizations && selectedOrgId === null) {
          const orgs = filterProdSupportOrgs(organizations);
          const orgIds = orgs ? Object.keys(orgs) : [];
          const needsOrgSelection = usesIdentitiesEndpoint && orgIds.length > 1;

          if (needsOrgSelection) {
            setOrgSelectionChoices(
              orgIds.map(id => ({
                id,
                name: orgs[id].name,
                roles: orgs[id].roles,
                employeeId: orgs[id].employeeId,
              }))
            );
            setOrgSelectionRequired(true);
          } else {
            // In the future this can be the last selected org saved in the /me endpoint
            // For now, we are automatically assuming the first org in the list.
            const organizationId = orgIds[0];

            if (orgs && orgIds.length > 0 && organizationId) {
              dispatch(
                organizationSelected({
                  organizationId: organizationId,
                  organizationUserRoles: orgs[organizationId].roles,
                  employeeId: orgs[organizationId].employeeId,
                })
              );
            }
          }
        }

        // This order is important to avoid a double call to the endpoint
        // set userDataRequestCompleted completed first, then set isRequestingUserData to false
        setUserDataRequestCompleted(true);
        setIsRequestingUserData(false);
      }
    };

    requestUserData();
  }, [
    dispatch,
    userDataRequestCompleted,
    isRequestingUserData,
    auth0UserAuthenticated,
    auth0User,
    loggedUser,
    getMyUserData,
    getMyUserIdentities,
    selectedOrgId,
  ]);

  return {
    isRequestingUserData,
    userDataRequestCompleted,
    myUserDataError: myUserDataError || myUserIdentitiesError,
    isUnauthorizedError,
    orgSelectionRequired,
    orgSelectionChoices,
  };
};
